import {
  useState, useEffect, useMemo,
} from 'react';
import {
  Form, Input,
} from '@skyline-platform/alchemy';
import {
  validateStringLength,
  validatePhoneNumber,
  validateEmail,
} from '@utils/formValidators';
import './ContactForm.css';
import * as is from 'is';
import PropTypes from 'prop-types';
import { getLogger } from '@utils/general';
import {
  saveContact,
  updateContact,
  getContacts,
} from '@utils/graphql/creators/contacts';
import { useDebouncedCallback } from 'use-debounce';
import { useFormSubmit } from '@utils/react/hooks/useFormSubmit';
import { FormFooter } from '../FormFooter/FormFooter';

const logger = getLogger('src.components.CameraForm');

export const ContactForm = ({
  contactData,
  handleClose,
  setNewCreatedContact,
}) => {
  const [firstName, setFirstName] = useState(contactData?.first_name || '');
  const [lastName, setLastName] = useState(contactData?.last_name || '');
  const [title, setTitle] = useState(contactData?.title || '');
  const [company, setCompany] = useState(contactData?.company_name || '');
  const [phoneNumber, setPhoneNumber] = useState(contactData?.primary_phone || '');
  const [email, setEmail] = useState(contactData?.email_address || '');
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  const fullName = `${firstName} ${lastName}`;

  const checkDuplicateEmail = async (email) => {
    const { query, execute } = getContacts(null, { emailAddress: email });
    query.addReturnProperties('first_name', 'last_name', 'id', 'email_address');

    await execute()
      .then(({ error, data }) => {
        if (error) {
          logger.error(error);
        }

        if (data && data.length > 0) {
          setDuplicateEmail(true);
        } else {
          setDuplicateEmail(false);
        }
      })
      .catch((err) => {
        logger.error(
          `There was a failure retrieving email: ${email}, error: ${err}`
        );
      });
  };

  // Derive error and allFieldsValid using useMemo
  const error = useMemo(() => {
    const firstNameValid = is.empty(firstName)
      ? { isValid: false, error: null }
      // Stryker disable next-line StringLiteral
      : validateStringLength(firstName, 1, 255, 'First Name');

    const lastNameValid = is.empty(lastName)
      ? { isValid: false, error: null }
      // Stryker disable next-line StringLiteral
      : validateStringLength(lastName, 1, 255, 'Last Name');

    // Stryker disable next-line StringLiteral
    const titleValid = validateStringLength(title, 0, 255, 'Title');

    const companyValid = is.empty(company)
      ? { isValid: false, error: null }
      // Stryker disable next-line StringLiteral
      : validateStringLength(company, 1, 255, 'Company');

    // Stryker disable next-line StringLiteral
    const phoneLengthValidation = validateStringLength(phoneNumber, 0, 15, 'Phone number');

    const phoneNumberValid = is.empty(phoneNumber)
      ? { isValid: false, error: null }
      : phoneLengthValidation.isValid
        ? validatePhoneNumber(phoneNumber)
        : phoneLengthValidation;

    // Stryker disable next-line StringLiteral
    const emailLengthValidation = validateStringLength(email, 0, 255, 'Email address');

    const emailValid = is.empty(email)
      ? { isValid: false, error: null }
      : duplicateEmail
        ? { isValid: false, error: 'Email address already exists. Please update the email address.' }
        : emailLengthValidation.isValid
          ? validateEmail(email)
          : emailLengthValidation;

    return {
      firstName: {
        isValid: firstNameValid.isValid,
        error: firstNameValid.error,
      },
      lastName: {
        isValid: lastNameValid.isValid,
        error: lastNameValid.error,
      },
      title: {
        isValid: titleValid.isValid,
        error: titleValid.error,
      },
      company: {
        isValid: companyValid.isValid,
        error: companyValid.error,
      },
      phoneNumber: {
        isValid: phoneNumberValid.isValid,
        error: phoneNumberValid.error,
      },
      email: {
        isValid: emailValid.isValid,
        error: emailValid.error,
      },
    };
  }, [
    firstName,
    lastName,
    title,
    company,
    phoneNumber,
    email,
    duplicateEmail,
  ]);

  const allFieldsValid = useMemo(() => (
    error.firstName.isValid &&
    error.lastName.isValid &&
    error.title.isValid &&
    error.company.isValid &&
    error.phoneNumber.isValid &&
    error.email.isValid
  ), [error]);

  const debouncedValidateEmail = useDebouncedCallback(checkDuplicateEmail, 300);

  useEffect(() => {
    if (contactData?.email_address.length === 0 || email.length === 0 || contactData?.email_address === email) {
      setTimeout(() => setDuplicateEmail(false), 0);
    } else {
      debouncedValidateEmail(email);
    }
  }, [email]);


  const submitContact = useFormSubmit({
    saveCreator: (payload) => saveContact('create_contact', payload),
    updateCreator: (id, payload) => updateContact('update_contact', id, payload),
    existingId: contactData?.id,
    logMessages: {
      success: `Successfully created contact ${fullName}.`,
      fail: `Failed to save contact ${fullName}.`,
    },
    updateLogMessages: {
      success: `Successfully updated contact ${fullName}.`,
      fail: `Failed to edit contact ${fullName}.`,
    },
    onSuccess: (data) => {
      if (typeof setNewCreatedContact === 'function') {
        setNewCreatedContact({ ...data, name: `${fullName} | ${email}` });
      }
    },
    handleClose,
  });

  const handleSave = () => {
    if (!allFieldsValid) return;
    return submitContact({
      firstName,
      lastName,
      title,
      companyName: company,
      primaryPhone: phoneNumber.toString(),
      emailAddress: email,
    });
  };

  return (
    <gar-contact-form>
      <Form
        formTitle='Create New Contact'
        footerPlacement='right'
        footer={[
          <FormFooter
            key='footer'
            onCancel={handleClose}
            onSubmit={handleSave}
            submitLabel={contactData ? 'Update Contact' : 'Create New Contact'}
            isValid={allFieldsValid}
          />,
        ]}
      >
        <Input
          id='input-first-name'
          inputLabel='First Name'
          helperText='First Name'
          autoComplete="name"
          value={firstName}
          key='input-first-name'
          onChange={(e) => setFirstName(e.target.value)}
          // Stryker disable next-line StringLiteral
          error={error.firstName.isValid ? undefined : 'true'}
          // Stryker disable next-line StringLiteral
          errorMessage={error.firstName.isValid ? '' : error.firstName.error}
          required
        />
        <Input
          id='input-last-name'
          inputLabel='Last Name'
          autoComplete="family-name"
          helperText='Last Name'
          value={lastName}
          key='input-last-name'
          onChange={(e) => setLastName(e.target.value)}
          // Stryker disable next-line StringLiteral
          error={error.lastName.isValid ? undefined : 'true'}
          // Stryker disable next-line StringLiteral
          errorMessage={error.lastName.isValid ? '' : error.lastName.error}
          required
        />
        <Input
          id='input-title'
          inputLabel='Title'
          autoComplete="organization-title"
          helperText='Title'
          value={title}
          key='input-title'
          onChange={(e) => setTitle(e.target.value)}
          // Stryker disable next-line StringLiteral
          error={error.title.isValid ? undefined : 'true'}
          // Stryker disable next-line StringLiteral
          errorMessage={error.title.isValid ? '' : error.title.error}
        />
        <Input
          id='input-company'
          inputLabel='Company'
          autoComplete="organization"
          helperText='Company'
          value={company}
          key='input-company'
          onChange={(e) => setCompany(e.target.value)}
          // Stryker disable next-line StringLiteral
          error={error.company.isValid ? undefined : 'true'}
          // Stryker disable next-line StringLiteral
          errorMessage={error.company.isValid ? '' : error.company.error}
          required
        />
        <Input
          id='input-phone-number'
          inputLabel='Phone Number'
          helperText='Phone Number'
          autoComplete="tel"
          value={phoneNumber}
          key='input-phone-number'
          // Stryker disable next-line StringLiteral
          type={'tel'}
          onChange={(e) => setPhoneNumber(e.target.value)}
          // Stryker disable next-line StringLiteral
          error={error.phoneNumber.isValid ? undefined : 'true'}
          // Stryker disable next-line StringLiteral
          errorMessage={error.phoneNumber.isValid ? '' : error.phoneNumber.error}
          required
        />
        <Input
          id='input-email'
          inputLabel='Email'
          helperText='Email'
          autoComplete="email"
          value={email}
          key='input-email'
          // Stryker disable next-line StringLiteral
          type={'email'}
          onChange={(e) => setEmail(e.target.value)}
          // Stryker disable next-line StringLiteral
          error={error.email.isValid ? undefined : 'true'}
          // Stryker disable next-line StringLiteral
          errorMessage={error.email.isValid ? '' : error.email.error}
          required
        />
      </Form>
    </gar-contact-form>
  );
};

ContactForm.propTypes = {
  contactData: PropTypes.object,
  handleClose: PropTypes.func,
  setNewCreatedContact: PropTypes.func,
};
